import React, { useEffect, useState, useCallback } from "react";
import { api, authHeaders, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldCheck, Loader2, Store, Pencil, CheckCircle2, Paperclip, X } from "lucide-react";
import { euro } from "@/lib/format";
import { fileToDataUrl } from "@/lib/image";
import { toast } from "sonner";

const computeDisc = (it, amt) => {
  if (it.discount_type === "percent") return Math.round(amt * (it.discount_value || 0)) / 100;
  if (it.discount_type === "fixed") return it.discount_value || 0;
  return it.discount_eur || 0; // price/bogo: keep the amount agreed at checkout
};

export default function PendingSpendGate() {
  const { client } = useAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [amt, setAmt] = useState("");
  const [receipt, setReceipt] = useState(null);

  const load = useCallback(async () => {
    if (!getToken("client")) { setItems([]); return; }
    try {
      const r = await api.get("/client/pending-confirmations", { headers: authHeaders("client") });
      setItems(r.data.pending || []);
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (!client) { setItems([]); return; }
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    const t = setInterval(load, 20000);
    return () => { window.removeEventListener("focus", onFocus); clearInterval(t); };
  }, [client, load]);

  const current = items[0];
  useEffect(() => { setEditing(false); setReceipt(null); setAmt(current ? String(current.amount_spent ?? "") : ""); }, [current?.claim_id]);

  if (!client || !current) return null;

  const pickReceipt = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const url = await fileToDataUrl(f, 1400, 0.8);
      setReceipt(url);
    } catch (_) { toast.error("Impossibile caricare l'immagine"); }
    e.target.value = "";
  };

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/client/claims/${current.claim_id}/amount/respond`, { action: "confirm" }, { headers: authHeaders("client") });
      toast.success("Spesa confermata");
      setItems((xs) => xs.slice(1));
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const saveModified = async () => {
    const v = parseFloat(amt);
    if (isNaN(v) || v < 0) return toast.error("Inserisci un importo valido");
    setBusy(true);
    try {
      await api.post(`/client/claims/${current.claim_id}/amount`,
        { amount_spent: Math.round(v * 100) / 100, discount_eur: Math.round(computeDisc(current, v) * 100) / 100, receipt: receipt || undefined },
        { headers: authHeaders("client") });
      toast.success("Correzione inviata al negozio. Deve accettarla.");
      setItems((xs) => xs.slice(1));
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const v = parseFloat(amt) || 0;
  const disc = computeDisc(current, editing ? v : (current.amount_spent || 0));
  const pays = Math.max(0, (editing ? v : (current.amount_spent || 0)) - disc);

  return (
    <div className="fixed inset-0 z-[3000] bg-slate-900/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="spend-gate">
      <div className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-5 kn-pop max-h-[92vh] overflow-y-auto">
        <div className="h-14 w-14 rounded-2xl bg-[#FFE4E6] flex items-center justify-center mx-auto mb-3"><ShieldCheck className="h-7 w-7 text-[#E63946]" /></div>
        <h2 className="text-xl font-extrabold text-slate-900 text-center">Conferma la tua spesa</h2>
        <p className="text-sm text-slate-500 text-center mt-1">Per continuare a usare l'app devi confermare (o correggere) l'importo registrato dal negozio.</p>
        {items.length > 1 && <p className="text-xs text-slate-400 text-center mt-1">{items.length} conferme in attesa</p>}

        <div className="mt-4 rounded-2xl border border-slate-100 p-4">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-slate-100 overflow-hidden flex items-center justify-center shrink-0">
              {current.logo ? <img src={current.logo} alt="" className="h-full w-full object-cover" /> : <Store className="h-5 w-5 text-slate-400" />}
            </div>
            <div className="min-w-0">
              <p className="font-bold text-slate-800 truncate">{current.store_name || "Negozio"}</p>
              <p className="text-xs text-slate-400 truncate">{current.title}</p>
            </div>
          </div>

          {!editing ? (
            <div className="mt-4 rounded-xl bg-emerald-50 p-3 text-emerald-800 text-sm font-bold" data-testid="spend-gate-summary">
              Hai speso {euro(current.amount_spent || 0)} · sconto {euro(current.discount_eur || 0)}<br />
              <span className="text-emerald-900">Hai pagato {euro(Math.max(0, (current.amount_spent || 0) - (current.discount_eur || 0)))}</span>
            </div>
          ) : (
            <div className="mt-4 space-y-2" data-testid="spend-gate-edit">
              <label className="text-sm font-semibold text-slate-600">Importo speso (€)</label>
              <Input type="number" min="0" step="0.01" value={amt} onChange={(e) => setAmt(e.target.value)}
                className="h-12 rounded-xl text-lg font-bold" data-testid="spend-gate-amount" autoFocus />
              <div className="rounded-xl bg-emerald-50 p-2.5 text-emerald-800 text-sm font-bold">Sconto {euro(disc)} · paghi {euro(pays)}</div>

              <div className="pt-1">
                <label className="text-sm font-semibold text-slate-600">Allega scontrino o fattura <span className="font-normal text-slate-400">(consigliato)</span></label>
                {receipt ? (
                  <div className="mt-2 relative inline-block">
                    <img src={receipt} alt="scontrino" className="h-28 w-28 rounded-xl object-cover border border-slate-200" data-testid="spend-gate-receipt-preview" />
                    <button type="button" onClick={() => setReceipt(null)} data-testid="spend-gate-receipt-remove"
                      className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-slate-900 text-white flex items-center justify-center shadow">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <label data-testid="spend-gate-receipt-btn"
                    className="mt-2 flex items-center justify-center gap-2 h-12 rounded-xl border-2 border-dashed border-slate-300 text-slate-600 font-semibold text-sm cursor-pointer hover:border-[#E63946] hover:text-[#E63946] transition-colors">
                    <Paperclip className="h-4 w-4" /> Carica foto dello scontrino
                    <input type="file" accept="image/*" capture="environment" className="hidden" onChange={pickReceipt} />
                  </label>
                )}
                <p className="text-[11px] text-slate-400 mt-1.5">La foto verrà inviata all'amministratore in caso di disaccordo con il negozio.</p>
              </div>
            </div>
          )}
        </div>

        {!editing ? (
          <div className="mt-4 space-y-2">
            <Button onClick={confirm} disabled={busy} data-testid="spend-gate-confirm" className="w-full h-12 rounded-xl font-bold gap-2">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />} Confermo, è corretto
            </Button>
            <Button variant="outline" onClick={() => setEditing(true)} disabled={busy} data-testid="spend-gate-modify" className="w-full h-12 rounded-xl font-bold gap-2">
              <Pencil className="h-4 w-4" /> L'importo non è corretto
            </Button>
            <p className="text-[11px] text-slate-400 text-center pt-1">Se l'importo non è corretto puoi proporne uno nuovo: il negozio dovrà accettarlo. In caso di disaccordo deciderà un amministratore.</p>
          </div>
        ) : (
          <div className="mt-4 space-y-2">
            <Button onClick={saveModified} disabled={busy} data-testid="spend-gate-save" className="w-full h-12 rounded-xl font-bold gap-2">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />} Invia correzione al negozio
            </Button>
            <p className="text-[11px] text-slate-400 text-center">Il negozio dovrà accettare la tua correzione. In caso di disaccordo deciderà un amministratore.</p>
            <Button variant="ghost" onClick={() => setEditing(false)} disabled={busy} className="w-full h-10 rounded-xl font-semibold text-slate-500">Annulla</Button>
          </div>
        )}
      </div>
    </div>
  );
}
